import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";

interface Song {
  title: string;
  artist: string;
  text: string;
  lyric?: string;
}

interface SetlistSectionProps {
  setList: Song[];
  onLyricOpen: (song: Song) => void;
  drawerOpen: boolean;
}

const SetlistSection: React.FC<SetlistSectionProps> = ({
  setList,
  onLyricOpen,
  drawerOpen,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // スクロールイベントでインジケーターを更新
  React.useEffect(() => {
    const container = document.getElementById("setlist-container");
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const containerWidth = container.offsetWidth;
        const newIndex = Math.round(scrollLeft / containerWidth);

        if (newIndex >= 0 && newIndex < setList.length) {
          setSelectedIndex(newIndex);
        }
      }, 50);
    };

    const handleResize = () => {
      clearTimeout(scrollTimeout);
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      const newIndex = Math.round(scrollLeft / containerWidth);

      if (newIndex >= 0 && newIndex < setList.length) {
        setSelectedIndex(newIndex);
      }
    };

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      clearTimeout(scrollTimeout);
    };
  }, [setList.length]);

  return (
    <Box>
      {/* スクロール可能なセットリスト */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mx: { xs: -2, md: -12 },
          height: "70vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            height: "100%",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            pointerEvents: drawerOpen ? "none" : "auto",
          }}
          id="setlist-container"
        >
          {setList.map((song, index) => (
            <Box
              key={index}
              sx={{
                minWidth: "100%",
                scrollSnapAlign: "start",
                scrollSnapStop: "always",
                px: { xs: 2, sm: 4, md: 8, lg: 12 },
                py: 3,
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  backgroundColor: "#ffffff",
                  width: "100%",
                  height: "100%",
                  maxWidth: { xs: "100%", sm: "600px", lg: "800px" },
                  mx: "auto",
                  borderRadius: 0,
                  borderLeft: "4px solid #b4bdbb",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* 曲番号 */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: { xs: 50, sm: 60 },
                    height: { xs: 50, sm: 60 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#e9f0ee",
                    borderBottomRightRadius: 24,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#53696d",
                      fontWeight: "bold",
                      fontFamily: "serif",
                      fontSize: { xs: "1.5rem", sm: "2rem" },
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>

                {/* コンテンツ */}
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    pt: { xs: 8, sm: 10 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* タイトルとアーティスト */}
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#53696d",
                        fontWeight: "bold",
                        mb: 0.5,
                        fontFamily: "serif",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {song.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#607d7d",
                        mb: 2,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {song.artist}
                    </Typography>
                  </Box>

                  {/* テキスト */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      marginTop: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      align="justify"
                      sx={{
                        color: "#2c3f42",
                        whiteSpace: "pre-line",
                        lineHeight: 1.6,
                        fontSize: {
                          xs: "0.9rem",
                          sm: song.text.length > 200 ? "0.875rem" : "1rem",
                          md: song.text.length > 200 ? "0.9rem" : "1rem",
                        },
                      }}
                    >
                      {song.text}
                    </Typography>
                  </Box>

                  {/* ボタン */}
                  {song.lyric?.trim() && (
                    <Box>
                      <Button
                        variant="text"
                        size="medium"
                        onClick={() => onLyricOpen(song)}
                        sx={{
                          color: "#53696d",
                          fontWeight: "bold",
                          textTransform: "none",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          padding: { xs: "6px 12px", sm: "8px 16px" },
                          "&:hover": {
                            backgroundColor: "#e9f0ee",
                          },
                        }}
                        endIcon={
                          <Typography
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            →
                          </Typography>
                        }
                      >
                        歌詞を読む
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* インジケーター */}
      <Box display="flex" justifyContent="center" mt={3} pb={2}>
        {setList.map((_, index) => (
          <Box
            key={index}
            onClick={() => {
              const container = document.getElementById("setlist-container");
              if (container) {
                const containerWidth = container.offsetWidth;
                container.scrollTo({
                  left: index * containerWidth,
                  behavior: "smooth",
                });
                setSelectedIndex(index);
              }
            }}
            sx={{
              width: 8,
              height: 8,
              mx: 0.5,
              borderRadius: "50%",
              backgroundColor: selectedIndex === index ? "#53696d" : "#b4bdbb",
              cursor: "pointer",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SetlistSection;
